import { useState, useEffect, useRef } from 'react'
import { useAuthStore } from '../hooks/useAuthStore'

/* ── Design tokens ──────────────────────────────────────── */
const C = {
  primary:            '#4800b2',
  primaryContainer:   '#6200ee',
  onPrimary:          '#ffffff',
  primaryFixed:       '#e8ddff',
  surface:            '#f8f9fd',
  surfaceContainerLowest: '#ffffff',
  surfaceContainerLow:    '#f2f3f7',
  surfaceContainerHigh:   '#e7e8ec',
  secondaryContainer: '#e3e0f4',
  secondary:          '#5e5d6d',
  onSurface:          '#191c1f',
  onSurfaceVariant:   '#494456',
  outline:            '#7a7488',
  outlineVariant:     '#cbc3d9',
  error:              '#ba1a1a',
  tertiary:           '#6f2300',
}

/* ── MCQ Detection ─────────────────────────────────────── */
function MCQOptions({ message, onSelectOption }) {
  const detectMCQ = (text) => {
    const cleanedText = text.replace(/\d{1,2}:\d{2}\s*(?:AM|PM)/gi, '').trim()
    const optionMatches = cleanedText.match(/[A-E]\.\s+.+/g)
    if (!optionMatches || optionMatches.length < 2) return null
    let firstOptionIndex = cleanedText.search(/\n[A-E]\.\s+/)
    if (firstOptionIndex === -1) return null
    const questionPart = cleanedText.substring(0, firstOptionIndex).trim()
    const optionsPart = cleanedText.substring(firstOptionIndex).trim()
    const options = []
    for (const line of optionsPart.split('\n')) {
      const m = line.trim().replace(/\d{1,2}:\d{2}\s*(?:AM|PM).*$/gi, '').trim().match(/^([A-E])\.\s+(.+)$/)
      if (m) options.push({ letter: m[1], text: m[2].trim() })
    }
    if (options.length < 2) return null
    return { question: questionPart, options }
  }

  const mcq = detectMCQ(message.text)
  if (!mcq) return <p style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>{message.text}</p>

  return (
    <div>
      <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{mcq.question}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {mcq.options.map((opt) => (
          <button key={opt.letter} onClick={() => onSelectOption(opt.letter, opt.text)} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '10px 14px',
            background: C.primaryFixed,
            border: `1px solid ${C.outlineVariant}`,
            borderRadius: 10, cursor: 'pointer', textAlign: 'left', width: '100%',
            transition: 'background 0.15s',
          }}>
            <span style={{
              flexShrink: 0, width: 24, height: 24, borderRadius: '50%',
              background: C.primaryContainer, color: '#fff',
              fontSize: 11, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{opt.letter}</span>
            <span style={{ fontSize: 13, color: C.onSurface, paddingTop: 2, lineHeight: 1.5 }}>{opt.text}</span>
          </button>
        ))}
      </div>
      <p style={{ fontSize: 11, color: C.outline, fontStyle: 'italic', marginTop: 8 }}>
        💡 Click an option to select your answer
      </p>
    </div>
  )
}

/* ── Main component ─────────────────────────────────────── */
export default function AskDrSamChat() {
  const { user } = useAuthStore()
  const [messages, setMessages] = useState([{
    id: 1,
    text: "Hello! I'm Dr. Sam, your AI medical assistant. How can I help you today?",
    sender: 'bot',
    timestamp: new Date(),
  }])
  const [inputMessage, setInputMessage]       = useState('')
  const [isLoading, setIsLoading]             = useState(false)
  const [error, setError]                     = useState('')
  const [previousResponseId, setPreviousResponseId] = useState(null)
  const [sessionId, setSessionId]             = useState(null)
  const [showSaveModal, setShowSaveModal]     = useState(false)
  const [letterToSave, setLetterToSave]       = useState('')
  const [isSaving, setIsSaving]               = useState(false)
  const [saveSuccess, setSaveSuccess]         = useState(false)
  const [sessionSeconds, setSessionSeconds]   = useState(0)
  const messagesEndRef   = useRef(null)
  const lastMessageRef   = useRef(null)
  const inputRef         = useRef(null)
  const textareaRef      = useRef(null)
  const timerRef         = useRef(null)

  // Scroll last bot message to top so question + options are visible
  useEffect(() => {
    const lastMsg = messages[messages.length - 1]
    if (lastMsg?.sender === 'bot' && lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])
  useEffect(() => { inputRef.current?.focus() }, [])

  // Session timer
  useEffect(() => {
    timerRef.current = setInterval(() => setSessionSeconds(s => s + 1), 1000)
    return () => clearInterval(timerRef.current)
  }, [])

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2,'0')}:${String(s % 60).padStart(2,'0')}`

  const getAuthToken = () => {
    try {
      const st = localStorage.getItem('auth-storage')
      if (st) return JSON.parse(st).state?.token || null
    } catch {}
    return null
  }

  const doSendMessage = async (text) => {
    if (!text.trim() || isLoading) return
    const userMsg = { id: Date.now(), text: text.trim(), sender: 'user', timestamp: new Date() }
    setMessages(prev => [...prev, userMsg])
    setInputMessage('')
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
    setIsLoading(true)
    setError('')
    try {
      const token = getAuthToken()
      if (!token) throw new Error('Authentication required. Please log in again.')
      const res = await fetch('/api/chat-responses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text.trim(), previousResponseId }),
      })
      if (!res.ok) {
        const ed = await res.json().catch(() => ({}))
        throw new Error(ed.error || `API error: ${res.status}`)
      }
      const data = await res.json()
      if (data.previousResponseId) setPreviousResponseId(data.previousResponseId)
      setMessages(prev => [...prev, { id: Date.now() + 1, text: data.answer, sender: 'bot', timestamp: new Date() }])
    } catch (err) {
      setError(err.message || 'Failed to get response. Please try again.')
      setMessages(prev => [...prev, { id: Date.now() + 1, text: "I'm sorry, I'm having trouble connecting right now. Please try again.", sender: 'bot', timestamp: new Date(), isError: true }])
    } finally {
      setIsLoading(false)
    }
  }

  const handleSendMessage = (e) => {
    e.preventDefault()
    doSendMessage(inputMessage)
  }

  const detectLetter = (text) => {
    const indicators = ['Dear Dr', 'Yours sincerely', 'Differential Diagnosis:', 'Suggested Investigations:', 'Re: Patient']
    return indicators.filter(i => text.toUpperCase().includes(i.toUpperCase())).length >= 3
  }

  const handleSaveLetter = (content) => { setLetterToSave(content); setShowSaveModal(true) }
  const handleClearChat  = () => {
    if (confirm('Clear the chat history?')) {
      setMessages([{ id: Date.now(), text: 'Chat cleared. How can I help you?', sender: 'bot', timestamp: new Date() }])
      setError(''); setPreviousResponseId(null); setSessionId(null)
    }
  }

  const saveLetter = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      const token = getAuthToken()
      const fd = new FormData(e.target)
      const res = await fetch('/api/chat-logs/save-letter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          sessionId,
          letterContent: letterToSave,
          patientInfo: { name: fd.get('patientName') || 'Patient', age: fd.get('patientAge') ? parseInt(fd.get('patientAge')) : null, gender: fd.get('patientGender') || null },
          recipientInfo: { name: fd.get('recipientName') || null, title: 'Dr', organization: fd.get('recipientOrg') || null },
        }),
      })
      if (!res.ok) throw new Error('Failed to save letter')
      setSaveSuccess(true)
      setTimeout(() => { setShowSaveModal(false); setSaveSuccess(false) }, 2000)
    } catch (err) { alert('Failed to save letter: ' + err.message) }
    finally { setIsSaving(false) }
  }

  const handleTextareaChange = (e) => {
    setInputMessage(e.target.value)
    e.target.style.height = 'auto'
    e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px'
  }

  const handleTextareaKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage(e)
    }
  }

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif", color: C.onSurface, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)', minHeight: 500 }}>

      {/* ── Session status bar ─────────────────────────────── */}
      <div style={{
        padding: '10px 20px',
        background: C.surfaceContainerLow + '80',
        borderBottom: `1px solid ${C.outlineVariant}22`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: C.primaryContainer, textTransform: 'uppercase', letterSpacing: 1.4 }}>Session Active</div>
            <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "'Manrope',sans-serif", color: C.onSurface }}>Ask Dr Sam — Chat Consult</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.surfaceContainerHigh, borderRadius: 9999, padding: '4px 12px' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.error, display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtTime(sessionSeconds)}</span>
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#dcfce7', borderRadius: 9999, padding: '3px 10px',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', letterSpacing: 0.5 }}>Responses API</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, justifyContent: 'flex-end' }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: 1.2 }}>Auto-Notes</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.primary }}>{Math.min(messages.length * 7, 95)}%</span>
            </div>
            <div style={{ width: 160, height: 4, background: C.surfaceContainerHigh, borderRadius: 9999, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: C.primary, width: `${Math.min(messages.length * 7, 95)}%`, borderRadius: 9999, transition: 'width 0.5s' }} />
            </div>
          </div>
          <button onClick={handleClearChat} style={{
            padding: '6px 8px', borderRadius: 8,
            background: C.surfaceContainerHigh, border: 'none',
            color: C.onSurfaceVariant, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className="uil uil-history" style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>

      {/* ── Chat workspace ─────────────────────────────────── */}
      <div style={{
        flex: 1, background: C.surfaceContainerLowest,
        borderLeft: `1px solid ${C.outlineVariant}22`,
        borderRight: `1px solid ${C.outlineVariant}22`,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {error && (
          <div style={{ background: '#fef2f2', borderLeft: `3px solid ${C.error}`, padding: '10px 20px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.error }}>
            <i className="uil uil-exclamation-circle" style={{ fontSize: 16, flexShrink: 0 }} />
            {error}
          </div>
        )}

        {/* Messages */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '24px 28px',
          display: 'flex', flexDirection: 'column', gap: 28,
          scrollbarWidth: 'thin', scrollbarColor: `${C.outlineVariant} transparent`,
        }}>
          {messages.map((msg, idx) => {
            const isUser = msg.sender === 'user'
            const isLast = idx === messages.length - 1
            return (
              <div key={msg.id} ref={isLast ? lastMessageRef : null} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
                <div style={{ display: 'flex', gap: 12, maxWidth: '85%', flexDirection: isUser ? 'row-reverse' : 'row', alignItems: 'flex-start' }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: isUser ? C.secondaryContainer : `${C.primaryContainer}22`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: isUser ? C.secondary : C.primaryContainer,
                  }}>
                    <i className={`uil ${isUser ? 'uil-user' : 'uil-robot'}`} style={{ fontSize: 18 }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: isUser ? 'row-reverse' : 'row' }}>
                      <span style={{ fontWeight: 700, fontSize: 12, fontFamily: "'Manrope',sans-serif", color: C.onSurface }}>
                        {isUser ? (user?.firstName || 'You') : 'Dr. Sam (AI)'}
                      </span>
                      <span style={{ fontSize: 10, color: `${C.onSurfaceVariant}99` }}>
                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      background: isUser ? C.primary : msg.isError ? '#fef2f2' : C.surfaceContainerLow,
                      color: isUser ? '#fff' : msg.isError ? C.error : C.onSurface,
                      boxShadow: isUser ? `0 2px 8px rgba(72,0,178,0.15)` : '0 2px 6px rgba(0,0,0,0.05)',
                      border: !isUser && !msg.isError ? `1px solid ${C.outlineVariant}44` : 'none',
                      fontSize: 14, lineHeight: 1.6,
                    }}>
                      {!isUser && !msg.isError ? (
                        <MCQOptions message={msg} onSelectOption={(letter) => doSendMessage(letter)} />
                      ) : (
                        <p style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.text}</p>
                      )}
                    </div>
                    {!isUser && !msg.isError && detectLetter(msg.text) && (
                      <button onClick={() => handleSaveLetter(msg.text)} style={{
                        marginTop: 4, padding: '5px 12px',
                        background: '#16a34a', color: '#fff',
                        border: 'none', borderRadius: 6, fontSize: 11,
                        cursor: 'pointer', fontWeight: 600,
                        alignSelf: 'flex-start',
                        display: 'flex', alignItems: 'center', gap: 5,
                      }}>
                        <i className="uil uil-save" style={{ fontSize: 13 }} />
                        Save to Patient File
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}

          {isLoading && (
            <div style={{ display: 'flex', gap: 12, maxWidth: '85%', alignItems: 'flex-start' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: `${C.primaryContainer}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.primaryContainer }}>
                <i className="uil uil-robot" style={{ fontSize: 18 }} />
              </div>
              <div style={{ padding: '14px 18px', borderRadius: '14px 14px 14px 4px', background: C.surfaceContainerLow, border: `1px solid ${C.outlineVariant}44`, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', gap: 5 }}>
                  {[0, 150, 300].map(delay => (
                    <div key={delay} style={{ width: 8, height: 8, background: C.outlineVariant, borderRadius: '50%', animation: 'bounce 1s infinite', animationDelay: `${delay}ms` }} />
                  ))}
                </div>
                <span style={{ fontSize: 13, color: C.onSurfaceVariant }}>Dr. Sam is thinking…</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* ── Input area ────────────────────────────────────── */}
        <div style={{ background: C.surfaceContainerLowest, borderTop: `1px solid ${C.outlineVariant}22`, padding: '16px 20px', flexShrink: 0 }}>
          <form onSubmit={handleSendMessage}>
            <div style={{
              position: 'relative', background: C.surfaceContainerHigh,
              borderRadius: 16, padding: '12px 16px',
              display: 'flex', alignItems: 'flex-end', gap: 10,
              transition: 'background 0.2s',
            }}>
              <button type="button" style={{ padding: 8, background: 'none', border: 'none', color: C.onSurfaceVariant, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                <i className="uil uil-notes" style={{ fontSize: 20 }} />
              </button>
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={handleTextareaChange}
                onKeyDown={handleTextareaKeyDown}
                placeholder="Ask Dr. Sam about symptoms, medications, or general health..."
                disabled={isLoading}
                rows={1}
                style={{
                  flex: 1, background: 'transparent', border: 'none',
                  outline: 'none', resize: 'none', fontSize: 14,
                  color: C.onSurface, lineHeight: 1.5, maxHeight: 128,
                  paddingTop: 4, fontFamily: "'Inter',sans-serif",
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginBottom: 2 }}>
                <button type="button" style={{ padding: 6, background: 'none', border: 'none', color: C.onSurfaceVariant, cursor: 'pointer', display: 'flex' }}>
                  <i className="uil uil-microphone" style={{ fontSize: 20 }} />
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !inputMessage.trim()}
                  style={{
                    width: 38, height: 38,
                    background: isLoading || !inputMessage.trim() ? C.outlineVariant : C.primaryContainer,
                    color: '#fff', border: 'none', borderRadius: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: isLoading || !inputMessage.trim() ? 'not-allowed' : 'pointer',
                    boxShadow: isLoading || !inputMessage.trim() ? 'none' : '0 2px 8px rgba(98,0,238,0.35)',
                    transition: 'all 0.15s', flexShrink: 0,
                  }}
                >
                  {isLoading ? (
                    <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                  ) : (
                    <i className="uil uil-message" style={{ fontSize: 17 }} />
                  )}
                </button>
              </div>
            </div>
          </form>

          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px' }}>
            <div style={{ display: 'flex', gap: 16 }}>
              {[
                { dot: C.primary,   label: 'AI Suggestion Active' },
                { dot: C.tertiary,  label: 'Secure HIPAA Line' },
                { dot: '#16a34a',   label: 'OpenAI Responses API' },
              ].map(({ dot, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 700, color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: 1.2 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, display: 'inline-block' }} />
                  {label}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, fontSize: 11, fontWeight: 700 }}>
              <button style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', padding: 0 }}>Quick Actions</button>
              <span style={{ color: `${C.onSurfaceVariant}44` }}>|</span>
              <button style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', padding: 0 }}>Session Summary</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Disclaimer ─────────────────────────────────────── */}
      <div style={{
        background: C.surfaceContainerHigh + '80',
        borderRadius: '0 0 12px 12px',
        padding: '10px 18px',
        display: 'flex', alignItems: 'flex-start', gap: 10,
        fontSize: 11, flexShrink: 0,
        border: `1px solid ${C.outlineVariant}22`,
        borderTop: 'none',
      }}>
        <i className="uil uil-info-circle" style={{ fontSize: 16, color: C.error, flexShrink: 0, marginTop: 1 }} />
        <p style={{ margin: 0, color: C.onSurfaceVariant, lineHeight: 1.6 }}>
          <strong style={{ color: C.onSurface }}>Medical Disclaimer: </strong>
          This AI assistant provides general medical information only — not a substitute for professional medical advice, diagnosis, or treatment. In emergencies, call your local emergency services immediately.
        </p>
      </div>

      {/* ── CSS ─────────────────────────────────────────────── */}
      <style>{`
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
        @keyframes pulse  { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>

      {/* ── Save Letter Modal ──────────────────────────────── */}
      {showSaveModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: C.onSurface, fontFamily: "'Manrope',sans-serif" }}>Save Letter to Patient File</h2>
                <button onClick={() => setShowSaveModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: C.outline }}>×</button>
              </div>
              {saveSuccess ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <i className="uil uil-check-circle" style={{ fontSize: 56, color: '#16a34a', display: 'block', marginBottom: 16 }} />
                  <h3 style={{ fontWeight: 700, color: C.onSurface, marginBottom: 8, fontFamily: "'Manrope',sans-serif" }}>Letter Saved!</h3>
                  <p style={{ color: C.onSurfaceVariant }}>The letter has been saved to the patient file.</p>
                </div>
              ) : (
                <form onSubmit={saveLetter}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 6, color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: 1 }}>Patient Name</label>
                      <input type="text" name="patientName" defaultValue={user?.firstName || ''} placeholder="Enter patient name"
                        style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${C.outlineVariant}`, borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: C.surfaceContainerHigh }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 6, color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: 1 }}>Patient Age</label>
                        <input type="number" name="patientAge" placeholder="Age"
                          style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${C.outlineVariant}`, borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: C.surfaceContainerHigh }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 6, color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: 1 }}>Gender</label>
                        <select name="patientGender" style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${C.outlineVariant}`, borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: C.surfaceContainerHigh }}>
                          <option value="">Select</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 6, color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: 1 }}>Referring Doctor (optional)</label>
                      <input type="text" name="recipientName" placeholder="Dr. Smith"
                        style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${C.outlineVariant}`, borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: C.surfaceContainerHigh }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 6, color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: 1 }}>Organization (optional)</label>
                      <input type="text" name="recipientOrg" placeholder="Medical Center"
                        style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${C.outlineVariant}`, borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: C.surfaceContainerHigh }} />
                    </div>
                    <div style={{ background: C.surfaceContainerLow, borderRadius: 10, padding: 14, maxHeight: 140, overflowY: 'auto' }}>
                      <p style={{ fontSize: 12, color: C.onSurfaceVariant, margin: 0, whiteSpace: 'pre-wrap' }}>{letterToSave.substring(0, 500)}…</p>
                    </div>
                    <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
                      <button type="submit" disabled={isSaving} style={{ flex: 1, padding: '11px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <i className="uil uil-save" style={{ fontSize: 15 }} />
                        {isSaving ? 'Saving…' : 'Save Letter'}
                      </button>
                      <button type="button" onClick={() => setShowSaveModal(false)} style={{ padding: '11px 22px', background: C.surfaceContainerHigh, color: C.onSurfaceVariant, border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}